import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import { Box, Stack, Typography } from "@mui/material";
import type { ElementType } from "react";

import { HOPE_SANS, HOPE_SERIF } from "@/shared/branding/thehopeonebrand";

import type { ImpactCardData } from "../utils/impactCardData";

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#5E8F72",
  sage: "#DCEADF",
  mist: "#EEF5EF",
  paper: "#FFFFFF",
  muted: "#5A6B63",
  border: "rgba(16,44,36,.10)",
};

const CATEGORY_VOICE: Record<string, { tagline: string }> = {
  education: { tagline: "Small support. Bigger tomorrows." },
  health: { tagline: "Care that reaches every door." },
  environment: { tagline: "What we plant today, they inherit." },
  water: { tagline: "Clean water changes everything." },
  food: { tagline: "No one should grow up hungry." },
  shelter: { tagline: "Everyone deserves a roof and a door." },
};

function voiceFor(category?: string) {
  const key = category?.trim().toLowerCase() ?? "";
  return CATEGORY_VOICE[key] ?? { tagline: "Small support. Bigger tomorrows." };
}

function IconBadge({ Icon, size = 42 }: { Icon: ElementType; size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: c.sage,
        color: c.forest,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: size * 0.48 }} />
    </Box>
  );
}

type ImpactCausePageProps = {
  title: string;
  description?: string;
  data: ImpactCardData;
  coverImageUrl?: string;
};

export default function ImpactCausePage({
  title,
  description,
  data,
}: ImpactCausePageProps) {
  const voice = voiceFor(data.causeCategory);
  const headline = data.causeSummary?.trim() || description?.trim() || title;
  const aboutBody =
    data.causeSummary && description && data.causeSummary.trim() !== description.trim()
      ? description.trim()
      : undefined;

  return (
    <Stack spacing={1.5}>
      <Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.2,
            py: 0.45,
            mb: 1.25,
            borderRadius: "999px",
            bgcolor: c.sage,
            color: c.forestDeep,
            fontFamily: HOPE_SANS,
            fontSize: "0.68rem",
            fontWeight: 800,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          <SpaRoundedIcon sx={{ fontSize: 14 }} />
          Cause
        </Box>
        {data.causeCategory ? (
          <Box
            sx={{
              display: "inline-flex",
              ml: 0.75,
              px: 1.15,
              py: 0.35,
              mb: 1.25,
              borderRadius: "999px",
              bgcolor: c.sage,
              color: c.forest,
              fontFamily: HOPE_SANS,
              fontSize: "0.68rem",
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {data.causeCategory}
          </Box>
        ) : null}
        <Typography
          component="h2"
          sx={{
            fontFamily: HOPE_SERIF,
            fontWeight: 700,
            fontSize: { xs: "1.8rem", sm: "2.45rem" },
            lineHeight: 1.05,
            letterSpacing: "-0.035em",
            color: c.forestDeep,
          }}
        >
          {title}
        </Typography>
        {data.location ? (
          <Stack direction="row" spacing={0.7} alignItems="flex-start" sx={{ mt: 1.1 }}>
            <LocationOnRoundedIcon sx={{ fontSize: 18, color: c.green, mt: "2px" }} />
            <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.92rem", color: c.muted, lineHeight: 1.45 }}>
              {data.location}
            </Typography>
          </Stack>
        ) : null}
        <Typography
          sx={{
            mt: 1.25,
            fontFamily: HOPE_SANS,
            fontSize: "0.68rem",
            fontWeight: 800,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: c.forest,
          }}
        >
          {voice.tagline}
        </Typography>
      </Box>

      <Box sx={{ p: 2, borderRadius: "22px", bgcolor: c.paper, border: `1px solid ${c.border}` }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <IconBadge Icon={MenuBookRoundedIcon} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: c.green, mb: 0.6 }}>
              About this project
            </Typography>
            <Typography
              sx={{
                fontFamily: HOPE_SERIF,
                fontWeight: 700,
                fontSize: { xs: "1.15rem", sm: "1.28rem" },
                lineHeight: 1.3,
                color: c.forestDeep,
              }}
            >
              {headline}
            </Typography>
            {aboutBody ? (
              <Typography sx={{ mt: 0.75, fontFamily: HOPE_SANS, fontSize: "0.88rem", lineHeight: 1.55, color: c.muted }}>
                {aboutBody}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 2, borderRadius: "22px", bgcolor: c.mist, border: `1px solid ${c.border}` }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <IconBadge Icon={TrackChangesRoundedIcon} size={36} />
          <Typography sx={{ fontFamily: HOPE_SANS, fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: c.forestDeep }}>
            The goal
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: HOPE_SERIF, fontSize: "1.02rem", lineHeight: 1.55, color: c.forestDeep }}>
          {data.impactGoal}
        </Typography>
      </Box>
    </Stack>
  );
}
